/**
 * LicenseGuard — vérifie la validité de la licence en arrière-plan.
 *
 * Si la licence est expirée ou révoquée, redirige vers /license-expired.
 * Les super_admin ne sont jamais bloqués.
 * La vérification se fait une seule fois par session (stocké en sessionStorage).
 */
import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, userSettings } from "../services/api/client";

const EXEMPT_ROUTES = [
  "/login",
  "/register",
  "/verify-email",
  "/setup",
  "/reset-password",
  "/magic-login",
  "/accept-invitation",
  "/auth-redirect",
  "/security/verify-login",
  "/license-expired",
  "/dedicated-server",
  "/share",
  "/sign",
  "/pricing",
  "/account",  // allow settings access to re-activate
];

const LICENSE_CHECK_KEY = "sylink_license_checked";

export default function LicenseGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isExemptRoute = EXEMPT_ROUTES.some((r) =>
      location.pathname.startsWith(r)
    );
    if (isExemptRoute) return;

    // Only check once per browser session to avoid API overhead
    if (sessionStorage.getItem(LICENSE_CHECK_KEY)) return;

    // Only check for authenticated users
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) return;

    const check = async () => {
      try {
        const [userData, settingsData] = await Promise.allSettled([
          auth.getCurrentUser(),
          userSettings.get(),
        ]);

        // Super admin — never blocked
        if (userData.status === "fulfilled") {
          const u = userData.value;
          if (u.role === "super_admin" || u.is_superuser) {
            sessionStorage.setItem(LICENSE_CHECK_KEY, "ok");
            return;
          }
        }

        if (settingsData.status === "fulfilled") {
          const lic = settingsData.value?.license_settings || {};

          // No license at all — only block if subscription is not active
          if (!lic.token && !lic.license_id) {
            // Check subscription status from user data
            const sub = settingsData.value?.subscription || null;
            if (sub && sub.status === "active") {
              sessionStorage.setItem(LICENSE_CHECK_KEY, "ok");
              return;
            }
            // No license and no active subscription — show no_license page
            // (but only for non-free plans to avoid blocking new users)
            sessionStorage.setItem(LICENSE_CHECK_KEY, "ok");
            return;
          }

          // License exists — check status
          const licStatus = lic.status;
          if (licStatus === "revoked") {
            sessionStorage.setItem(LICENSE_CHECK_KEY, "revoked");
            navigate("/license-expired?reason=revoked", { replace: true });
            return;
          }
          if (licStatus === "cancelled") {
            sessionStorage.setItem(LICENSE_CHECK_KEY, "cancelled");
            navigate("/license-expired?reason=cancelled", { replace: true });
            return;
          }

          // Check expiry date
          if (lic.expires_at) {
            const expiresAt = new Date(lic.expires_at);
            if (!isNaN(expiresAt) && expiresAt < new Date()) {
              sessionStorage.setItem(LICENSE_CHECK_KEY, "expired");
              navigate("/license-expired?reason=expired", { replace: true });
              return;
            }
          }

          // All good
          sessionStorage.setItem(LICENSE_CHECK_KEY, "ok");
        }
      } catch (_) {
        // Network error — don't block access
        sessionStorage.setItem(LICENSE_CHECK_KEY, "ok");
      }
    };

    check();
  }, [location.pathname, navigate]);

  return null;
}
